
import React, { useState, useEffect } from 'react';
import getImageUrl from '../../utils/getImageUrl';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Typography,
  Alert,
  Paper,
  Box,
  CircularProgress,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';

const ProductEditModal = ({ open, onClose, product, onSave }) => {
  const [editedProduct, setEditedProduct] = useState(product || {});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadLoading, setUploadLoading] = useState({});
  
  // Create refs for each aroma's file input
  const fileInputRefs = React.useRef([]);

  // Initialize refs when product changes
  useEffect(() => {
    if (product?.aromas) {
      fileInputRefs.current = product.aromas.map(() => React.createRef());
    }
  }, [product]);

  // Reset editedProduct when product prop changes
 useEffect(() => {
  if (product) {
    setEditedProduct({
      ...product,
      keyFeatures: product.keyFeatures || '',
      ingredients: product.ingredients || '',
      howToUse: product.howToUse || '',
      customerReviews: product.customerReviews || '',
    });
  }
}, [product]);


  // Don't render if no product is selected
  if (!product) return null;

  const resolveImageUrl = (imagePath) => getImageUrl(imagePath) || "/api/placeholder/48/48";

  const handleBasicFieldChange = (field, value) => {
    setEditedProduct(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAromaNameChange = (aromaIndex, newName) => {
    setEditedProduct(prev => {
      const updated = {...prev};
      updated.aromas[aromaIndex].name = newName;
      return updated;
    });
  };

  const handleAddAroma = () => {
    const tempId = `temp-${Date.now()}`;
    setEditedProduct(prev => ({
      ...prev,
      aromas: [
        ...prev.aromas,
        {
          _id: tempId,
          name: '',
          images: [],
          sizes: []
        }
      ]
    }));
    
    // Add a new ref for the new aroma
    fileInputRefs.current = [...fileInputRefs.current, React.createRef()];
  };

  const handleRemoveAroma = (aromaIndex) => {
    setEditedProduct(prev => {
      const updated = {...prev};
      updated.aromas.splice(aromaIndex, 1);
      return updated;
    });
    
    // Remove the ref for this aroma
    fileInputRefs.current.splice(aromaIndex, 1);
  };

  const handleRemoveImage = async (aromaIndex, imageIndex) => {
    const loadingKey = `${aromaIndex}-${imageIndex}`;
    setUploadLoading(prev => ({ ...prev, [loadingKey]: true }));
    
    try {
      const response = await fetch(`/api/products/${product._id}/images`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ aromaIndex, imageIndex })
      });

      if (!response.ok) {
        throw new Error('Failed to delete image');
      }

      setEditedProduct(prev => {
        const updated = {...prev};
        updated.aromas[aromaIndex].images.splice(imageIndex, 1);
        return updated;
      });
    } catch (error) {
      setError('Failed to delete image. Please try again.');
    } finally {
      setUploadLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleAddImage = async (aromaIndex, files) => {
    setUploadLoading(prev => ({ ...prev, [aromaIndex]: true }));
    
    const formData = new FormData();
    formData.append('aromaIndex', aromaIndex);
    Array.from(files).forEach(file => {
      formData.append('images', file);
    });

    try {
      const response = await fetch(`/api/products/${product._id}/images`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload images');
      }

      const data = await response.json();
      
      setEditedProduct(prev => {
        const updated = {...prev};
        updated.aromas[aromaIndex].images = [
          ...(updated.aromas[aromaIndex].images || []),
          ...data.imageUrls
        ];
        return updated;
      });
    } catch (error) {
      setError('Failed to upload images. Please try again.');
    } finally {
      setUploadLoading(prev => ({ ...prev, [aromaIndex]: false }));
    }
  };

  const handleSizeChange = (aromaIndex, sizeIndex, field, value) => {
    setEditedProduct(prev => {
      const updated = {...prev};
      const size = updated.aromas[aromaIndex].sizes[sizeIndex];
      size[field] = value;

      if (field === 'price' || field === 'offer') {
        const price = parseFloat(size.price || 0);
        const offer = parseFloat(size.offer || 0);
        size.sellingPrice = Math.floor(price - (price * (offer / 100)));
      }

      // Generate sizeId when size or unit changes
      if (field === 'size' || field === 'unit') {
        const productCode = updated.productCode || '';
        const aromaName = updated.aromas[aromaIndex].name || '';
        // Create abbreviated aroma name (first letter of each word)
        const aromaCode = aromaName
          .split(' ')
          .map(word => word.charAt(0).toUpperCase())
          .join('');
        const sizeUnit = size.unit ? size.unit.toUpperCase() : '';
        size.sizeId = `${updated.categoryCode}-${productCode}-${aromaCode}-${size.size}${sizeUnit}`;
      }

      return updated;
    });
  };

  const handleAddSize = (aromaIndex) => {
    setEditedProduct(prev => {
      const updated = {...prev};
      updated.aromas[aromaIndex].sizes.push({
        size: '',
        unit: '',
        quantity: 0,
        price: 0,
        offer: 0,
        sellingPrice: 0
      });
      return updated;
    });
  };

  const handleRemoveSize = (aromaIndex, sizeIndex) => {
    setEditedProduct(prev => {
      const updated = {...prev};
      updated.aromas[aromaIndex].sizes.splice(sizeIndex, 1);
      return updated;
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      await onSave(editedProduct);
      onClose();
    } catch (error) {
      setError('Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Edit Product: {editedProduct.productName}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Basic Product Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product Name"
                value={editedProduct.productName || ''}
                onChange={(e) => handleBasicFieldChange('productName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product Code"
                value={editedProduct.productCode || ''}
                onChange={(e) => handleBasicFieldChange('productCode', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={editedProduct.description || ''}
                onChange={(e) => handleBasicFieldChange('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Uses"
                multiline
                rows={2}
                value={editedProduct.uses || ''}
                onChange={(e) => handleBasicFieldChange('uses', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Caption"
                value={editedProduct.caption || ''}
                onChange={(e) => handleBasicFieldChange('caption', e.target.value)}
              />
            </Grid>
                <Grid item xs={12}>
      <TextField
        fullWidth
        label="Key Features"
        multiline
        rows={2}
        value={editedProduct.keyFeatures || ''}
        onChange={(e) => handleBasicFieldChange('keyFeatures', e.target.value)}
      />
    </Grid>

    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Ingredients"
        multiline
        rows={2}
        value={editedProduct.ingredients || ''}
        onChange={(e) => handleBasicFieldChange('ingredients', e.target.value)}
      />
    </Grid>

    <Grid item xs={12}>
      <TextField
        fullWidth
        label="How to Use"
        multiline
        rows={2}
        value={editedProduct.howToUse || ''}
        onChange={(e) => handleBasicFieldChange('howToUse', e.target.value)}
      />
    </Grid>

    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Customer Reviews"
        multiline
        rows={3}
        value={editedProduct.customerReviews || ''}
        onChange={(e) => handleBasicFieldChange('customerReviews', e.target.value)}
      />
    </Grid>

          </Grid>
        </Paper>

        {/* Aromas Section */}
        <Box sx={{ mb: 2 }}>
     

          {editedProduct.aromas?.map((aroma, aromaIndex) => (
            <Paper key={aroma._id || aromaIndex} sx={{ p: 3, mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <TextField
                  sx={{ flexGrow: 1, mr: 2 }}
                  value={aroma.name}
                  onChange={(e) => handleAromaNameChange(aromaIndex, e.target.value)}
                  label="Aroma Name"
                  variant="outlined"
                />
                <IconButton
                  color="error"
                  onClick={() => handleRemoveAroma(aromaIndex)}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                {aroma.images?.map((image, imageIndex) => (
                  <Grid item xs={12} sm={6} md={4} key={imageIndex}>
                    <Box 
                      sx={{ 
                        position: 'relative',
                        width: '100%',
                        paddingTop: '75%',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        component="img"
                        src={resolveImageUrl(image)}
                        alt={`${aroma.name} ${imageIndex + 1}`}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                      />
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'error.main',
                          '&:hover': { bgcolor: 'error.dark' },
                          color: 'white',
                          zIndex: 1
                        }}
                        size="small"
                        onClick={() => handleRemoveImage(aromaIndex, imageIndex)}
                        disabled={uploadLoading[`${aromaIndex}-${imageIndex}`]}
                      >
                        {uploadLoading[`${aromaIndex}-${imageIndex}`] ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <CloseIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <input
                type="file"
                ref={fileInputRefs.current[aromaIndex]}
                hidden
                multiple
                accept="image/*"
                onChange={(e) => handleAddImage(aromaIndex, e.target.files)}
              />
              
              <Button
                variant="outlined"
                fullWidth
                onClick={() => fileInputRefs.current[aromaIndex].current.click()}
                disabled={uploadLoading[aromaIndex]}
                startIcon={uploadLoading[aromaIndex] ? (
                  <CircularProgress size={20} />
                ) : (
                  <ImageIcon />
                )}
                sx={{ mb: 3 }}
              >
                Add Images
              </Button>

              <Typography variant="subtitle1" sx={{ mb: 2 }}>Sizes</Typography>
              
              {aroma.sizes?.map((size, sizeIndex) => (
                <Grid container spacing={2} sx={{ mb: 2 }} key={sizeIndex} alignItems="center">
                  <Grid item xs={2}>
                    <TextField
                      fullWidth
                      value={size.size}
                      onChange={(e) => handleSizeChange(aromaIndex, sizeIndex, 'size', e.target.value)}
                      label="Size"
                      variant="outlined"
                    />
                  </Grid>
                  
                  <Grid item xs={2}>
                    <FormControl fullWidth>
                      <InputLabel>Unit</InputLabel>
                      <Select
                        value={size.unit}
                        onChange={(e) => handleSizeChange(aromaIndex, sizeIndex, 'unit', e.target.value)}
                        label="Unit"
                      >
                        <MenuItem value="ml">ml</MenuItem>
                        <MenuItem value="L">L</MenuItem>
                        <MenuItem value="mg">mg</MenuItem>
                        <MenuItem value="g">g</MenuItem>
                        <MenuItem value="kg">kg</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={2}>
                    <TextField
                      fullWidth
                      type="number"
                      value={size.quantity}
                      onChange={(e) => handleSizeChange(aromaIndex, sizeIndex, 'quantity', e.target.value)}
                      label="Quantity"
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <TextField
                      fullWidth
                      type="number"
                      value={size.price}
                      onChange={(e) => handleSizeChange(aromaIndex, sizeIndex, 'price', e.target.value)}
                      label="Price"
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <TextField
                      fullWidth
                      type="number"
                      value={size.offer}
                      onChange={(e) => handleSizeChange(aromaIndex, sizeIndex, 'offer', e.target.value)}
                      label="Offer %"
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Size ID:
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                          {size.sizeId || '-'}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          ₹{size.sellingPrice}
                        </Typography>
                      </Box>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemoveSize(aromaIndex, sizeIndex)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              ))}

              <Button
                variant="outlined"
                fullWidth
                onClick={() => handleAddSize(aromaIndex)}
                startIcon={<AddIcon />}
              >
                Add Size
              </Button>
            </Paper>
          ))}
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Aromas</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddAroma}
            >
              Add Aroma
            </Button>
          </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductEditModal;